import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button } from "waapi/waap-ui/components/Button/Button.js";
import { ICONSAX_SIZE } from "waapi/waap-ui/components/Iconsax/Iconsax.types.js";
import { ExportArrow01Linear } from "waapi/waap-ui/components/Iconsax/ExportArrow01Linear.js";
import { ProfileBold } from "waapi/waap-ui/components/Iconsax/ProfileBold.js";
import { SettingGear2Bold } from "waapi/waap-ui/components/Iconsax/SettingGear2Bold.js";
import { ToRightArrowBold } from "waapi/waap-ui/components/Iconsax/ToRightArrowBold.js";
import { Wallet2Bold } from "waapi/waap-ui/components/Iconsax/Wallet2Bold.js";
import { Popover } from "waapi/waap-ui/components/Popover/Popover.js";
import { PopoverUtils } from "waapi/waap-ui/components/Popover/Popover.types.js";
import { TextSize, TextWeight } from "waapi/waap-ui/components/Text/Text.types.js";
import { useCopyToClipboard } from "../../../customHooks/useCopyToClipboard.js";
import { useTranslation } from "../../../customHooks/useTranslation/index.js";
import { useLinks } from "../../../entities/Link/hooks/useLinks/index.js";
import { getMyIdentity, getUser } from "../../../entities/User/hooks/useUserState/selectors.js";
import { CopyMyAffiliationCode } from "../../components/CopyMyAffiliationCode/index.js";
import { DropdownActions, DropdownActionsProps } from "../../components/DropdownActions/index.js";
import { MyAvatar } from "../../components/MyAvatar/index.js";
import HeaderMask from "./mask.svg?react";
import { HeaderDropdownIcon, StyledDropdownDiv, StyledHeader, StyledHeaderInner, StyledUserMenu, UserName } from "./style.js";

export const Header = (): JSX.Element => {
    const { t, translations } = useTranslation();
    const identity = useSelector(getMyIdentity);
    const user = useSelector(getUser);
    const navigate = useNavigate();
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const popoverRef = useRef<PopoverUtils>(null);
    const { links, loading } = useLinks({ filters: {} });

    const actionsDropdown: DropdownActionsProps["actions"] = {
        billing: {
            label: t(translations.header.actions.billing()),
            fn: () => {
                navigate("/settings/bills");
                if (popoverRef?.current) popoverRef.current.close();
            },
            icon: Wallet2Bold,
        },
        settings: {
            label: t(translations.header.actions.settings()),
            fn: () => {
                navigate("/settings/my-account");
                if (popoverRef?.current) popoverRef.current.close();
            },
            icon: SettingGear2Bold,
        },
        myAccount: {
            label: t(translations.header.actions.myAccount()),
            fn: () => {
                navigate("/settings/my-account");
                if (popoverRef?.current) popoverRef.current.close();
            },
            icon: ProfileBold,
        },
        logout: {
            label: t(translations.header.actions.logout()),
            fn: () => {
                navigate("/logout");
                if (popoverRef?.current) popoverRef.current.close();
            },
            icon: ExportArrow01Linear,
        },
    };

    const { copyToClipboard } = useCopyToClipboard();

    const copyDefaultLink = () => {
        const landingLink = links.find((link) => link.tag === "landing");
        if (landingLink) {
            copyToClipboard({
                textToCopy: landingLink.shortUrl,
                textFailed: t(translations.header.shareLink.failed()),
                textSuccess: t(translations.header.shareLink.success()),
            });
        }
    };

    return (
        <>
            <HeaderMask />
            <StyledHeader>
                <StyledHeaderInner>
                    <Button loading={loading} iconLeft={ToRightArrowBold} size="small" onClick={copyDefaultLink}>
                        {t(translations.header.shareLink.button())}
                    </Button>
                    {user?.affiliation?.tracking && <CopyMyAffiliationCode affiliationCode={user?.affiliation?.tracking} />}
                    <Popover
                        onOpenChange={setIsDropdownOpen}
                        dropdownContent={<DropdownActions actions={actionsDropdown} noDivider />}
                        ref={popoverRef}
                    >
                        <StyledUserMenu isDropdownOpen={isDropdownOpen}>
                            <MyAvatar size="small" />
                            <UserName size={TextSize.SMALL} weight={TextWeight.BOLD}>
                                {identity}
                            </UserName>
                            <StyledDropdownDiv>
                                <HeaderDropdownIcon size={ICONSAX_SIZE.SMALL} />
                            </StyledDropdownDiv>
                        </StyledUserMenu>
                    </Popover>
                </StyledHeaderInner>
            </StyledHeader>
        </>
    );
};
